import axios from 'axios';
import fallbackAds from '../data/ads.json';

const IMPRESSION_COOLDOWN_MS = 1000 * 60 * 30;
const ADMOB_TEST_INTERSTITIAL_ID = 'ca-app-pub-3940256099942544/1033173712';
const ADMOB_INTERSTITIAL_ID = (process.env.REACT_APP_ADMOB_INTERSTITIAL_ID || '').trim();
const ADMOB_IS_TESTING = String(process.env.REACT_APP_ADMOB_IS_TESTING || 'false').toLowerCase() === 'true';
const ADMOB_TESTING_DEVICES = String(process.env.REACT_APP_ADMOB_TESTING_DEVICES || '')
  .split(',')
  .map((entry) => entry.trim())
  .filter(Boolean);
const FORBIDDEN_AD_KEYWORDS = [
  'porn',
  'porno',
  'xxx',
  'adult',
  '18+',
  'sex',
  'sexy',
  'escort',
  'camgirl',
  'webcam live',
  'onlyfans',
  'xvideos',
  'pornhub',
  'xnxx',
  'hentai',
  'fetish',
  'nude',
  'naked',
  'erotic',
  'alcool',
  'alcohol',
  'vin',
  'wine',
  'biere',
  'beer',
  'whisky',
  'whiskey',
  'vodka',
  'cocktail',
  'champagne',
  'casino',
  'bet',
  'betting',
  'gambling',
  'poker',
  'dating',
  'hookup',
  'violent',
  'violence',
  'weapon',
  'guns',
];

const FORBIDDEN_AD_DOMAINS = [
  'pornhub.com',
  'xvideos.com',
  'xnxx.com',
  'xhamster.com',
  'youporn.com',
  'redtube.com',
  'onlyfans.com',
  'chaturbate.com',
  'stripchat.com',
  'camsoda.com',
  'livejasmin.com',
  'adultfriendfinder.com',
  'bet365.com',
  'unibet.com',
  'pokerstars.com',
  'tinder.com',
  'bumble.com',
];

function getStorageValue(key, fallbackValue) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallbackValue;
  } catch {
    return fallbackValue;
  }
}

function setStorageValue(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage failures to keep UX smooth.
  }
}

function normalizeAdText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function hasForbiddenKeyword(ad) {
  const textBlob = [ad?.title, ad?.description, ad?.sponsor, ad?.ctaText, ad?.ctaUrl]
    .map((entry) => normalizeAdText(entry))
    .join(' ');

  return FORBIDDEN_AD_KEYWORDS.some((keyword) => textBlob.includes(keyword));
}

function hasForbiddenDomain(ad) {
  const ctaUrl = normalizeAdText(ad?.ctaUrl);
  const imageUrl = normalizeAdText(ad?.imageUrl);
  const payload = `${ctaUrl} ${imageUrl}`;
  return FORBIDDEN_AD_DOMAINS.some((domain) => payload.includes(domain));
}

function sanitizeAds(list) {
  if (!Array.isArray(list)) return [];

  return list
    .map((ad) => {
      if (ad?.provider !== 'google_admob') return ad;

      const resolvedAdUnitId = ADMOB_INTERSTITIAL_ID || String(ad?.adUnitId || '').trim() || ADMOB_TEST_INTERSTITIAL_ID;
      return {
        ...ad,
        adUnitId: resolvedAdUnitId,
      };
    })
    .filter((ad) => {
      if (!ad?.id || !ad?.title) return false;
      if (hasForbiddenKeyword(ad)) return false;
      if (hasForbiddenDomain(ad)) return false;
      return true;
    });
}

export async function loadAds() {
  try {
    const response = await axios.get('/ads/active');
    if (Array.isArray(response.data) && response.data.length > 0) {
      return sanitizeAds(response.data);
    }
  } catch {
    // Fallback handled below.
  }
  return sanitizeAds(fallbackAds);
}

export async function trackAdEvent(type, ad) {
  if (!ad?.id) return;

  const now = Date.now();
  if (type === 'impression') {
    const lastImpressions = getStorageValue('smartinventory_ad_impressions', {});
    const lastTimestamp = Number(lastImpressions[ad.id] || 0);
    if (now - lastTimestamp < IMPRESSION_COOLDOWN_MS) return;
    lastImpressions[ad.id] = now;
    setStorageValue('smartinventory_ad_impressions', lastImpressions);
  }

  try {
    await axios.post('/ads/track', {
      ad_id: ad.id,
      provider: ad.provider || 'direct',
      event_type: type,
      occurred_at: new Date(now).toISOString(),
    });
    return;
  } catch {
    // Save locally if backend tracking endpoint is unavailable.
  }

  const localEvents = getStorageValue('smartinventory_ad_events', []);
  localEvents.push({
    ad_id: ad.id,
    provider: ad.provider || 'direct',
    event_type: type,
    occurred_at: new Date(now).toISOString(),
  });
  setStorageValue('smartinventory_ad_events', localEvents.slice(-500));
}

export function isNativeAndroidApp() {
  try {
    const cap = window?.Capacitor;
    if (!cap || typeof cap.getPlatform !== 'function') return false;
    return cap.getPlatform() === 'android';
  } catch {
    return false;
  }
}

export async function showAdMobInterstitial(adUnitId) {
  try {
    const cap = window?.Capacitor;
    const adMobPlugin = cap?.Plugins?.AdMob;
    const resolvedAdUnitId = String(adUnitId || '').trim();
    if (!adMobPlugin || !resolvedAdUnitId || !isNativeAndroidApp()) {
      return false;
    }

    if (typeof adMobPlugin.initialize === 'function') {
      await adMobPlugin.initialize({
        requestTrackingAuthorization: false,
        testingDevices: ADMOB_TESTING_DEVICES,
        initializeForTesting: ADMOB_IS_TESTING,
      });
    }

    await adMobPlugin.prepareInterstitial({ adId: resolvedAdUnitId, isTesting: ADMOB_IS_TESTING });
    await adMobPlugin.showInterstitial();
    return true;
  } catch {
    return false;
  }
}
